import {
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Card, Col, Descriptions, Row, Tabs, Tag } from 'antd';
import ApplicationsList from 'containers/Applications/List';
import CareerRequestsList from 'containers/CareerRequests/List';
import RestShow from 'containers/rest/Show';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const UsersShowUI = ({ record }) => {
  const { t } = useTranslation();
  const roles = useSelector((state) => state.config.data?.roles) || [];

  if (!record) return null;

  // Get role label
  const getRoleLabel = (roleValue) => {
    if (!roleValue) return 'N/A';
    const role = roles.find((r) => r.value === roleValue);
    return role ? role.label : roleValue;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY HH:mm');
  };

  // Format date of birth
  const formatDOB = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY');
  };

  // Construct full name
  const fullName = record?.fullName ||
    `${record?.firstName || ''} ${record?.lastName || ''}`.trim() ||
    'N/A';

  // Check if phoneNumber looks like a valid phone (not a password)
  const isValidPhone = (phone) => {
    if (!phone) return false;
    // If it contains @, it's likely not a phone number
    if (phone.includes('@')) return false;
    // Basic check: should contain numbers
    return /\d/.test(phone);
  };

  // Filter applications by user email
  const applicationsFilter = record?.email
    ? {
      filter: {
        email: record.email
      },
      join: ['recruitment', 'recruitment.company'],
    }
    : {
      filter: {},
      join: ['recruitment', 'recruitment.company'],
    };

  // Filter career requests by user email
  const careerRequestsFilter = record?.email
    ? {
      filter: {
        email: record.email,
      },
    }
    : {
      filter: {},
    };

  const tabItems = [
    {
      key: 'applications',
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Đơn Ứng Tuyển
        </span>
      ),
      children: (
        <ApplicationsList
          initialFilter={applicationsFilter}
          hasSearch={false}
          hasCreate={false}
        />
      ),
    },
    {
      key: 'career-requests',
      label: (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          Yêu Cầu Tư Vấn
        </span>
      ),
      children: (
        <CareerRequestsList
          initialFilter={careerRequestsFilter}
          hasSearch={false}
          hasCreate={false}
        />
      ),
    },
  ];

  return (
    <Row gutter={[16, 16]} className="w-full">
      <Col xs={24}>
        <Card title={t('users.generalInformation')} bordered={false}>
          <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('users.fullName')}>
              {fullName}
            </Descriptions.Item>
            <Descriptions.Item label={t('users.id')}>
              #{record?.id || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={t('users.firstName')}>
              {record?.firstName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={t('users.lastName')}>
              {record?.lastName || 'N/A'}
            </Descriptions.Item>
            {record?.username && (
              <Descriptions.Item label={t('users.username')}>
                {record.username}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={
                <span>
                  <MailOutlined style={{ marginRight: 8 }} />
                  {t('users.email')}
                </span>
              }
            >
              {record?.email || 'N/A'}
            </Descriptions.Item>
            {isValidPhone(record?.phoneNumber) && (
              <Descriptions.Item
                label={
                  <span>
                    <PhoneOutlined style={{ marginRight: 8 }} />
                    {t('users.phone')}
                  </span>
                }
              >
                {record.phoneNumber}
              </Descriptions.Item>
            )}
            {record?.dob && (
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    Ngày sinh
                  </span>
                }
              >
                {formatDOB(record.dob)}
              </Descriptions.Item>
            )}
            {record?.gender && (
              <Descriptions.Item label={t('users.gender')}>
                {record.gender === 'male' || record.gender === 'Male'
                  ? t('users.male')
                  : record.gender === 'female' || record.gender === 'Female'
                    ? t('users.female')
                    : record.gender}
              </Descriptions.Item>
            )}
            {record?.address && (
              <Descriptions.Item
                label={
                  <span>
                    <HomeOutlined style={{ marginRight: 8 }} />
                    Địa chỉ
                  </span>
                }
              >
                {record.address}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={
                <span>
                  <SafetyOutlined style={{ marginRight: 8 }} />
                  {t('users.role')}
                </span>
              }
            >
              <Tag color="blue">{getRoleLabel(record?.role)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('users.isActive')}>
              <Tag color={record?.isActive ? 'green' : 'red'}>
                {record?.isActive ? t('status.active') : t('status.inactive')}
              </Tag>
            </Descriptions.Item>
            {typeof record?.isJobSearching === 'boolean' && (
              <Descriptions.Item label="Trạng thái tìm việc">
                <Tag color={record.isJobSearching ? 'green' : 'default'}>
                  {record.isJobSearching ? (
                    <span>
                      <CheckCircleOutlined style={{ marginRight: 4 }} />
                      Đang tìm việc
                    </span>
                  ) : (
                    'Không tìm việc'
                  )}
                </Tag>
              </Descriptions.Item>
            )}
            {record?.cvLink && (
              <Descriptions.Item
                label={
                  <span>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    CV Link
                  </span>
                }
              >
                <a href={record.cvLink} target="_blank" rel="noopener noreferrer">
                  Xem CV
                </a>
              </Descriptions.Item>
            )}
            {record?.defaultCvId && (
              <Descriptions.Item label="CV Mặc định">
                ID: {record.defaultCvId}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label={
                <span>
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  {t('users.createdAt')}
                </span>
              }
            >
              {formatDate(record?.createdAt)}
            </Descriptions.Item>
            {record?.updatedAt && (
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    Cập nhật lần cuối
                  </span>
                }
              >
                {formatDate(record.updatedAt)}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </Col>

      {/* Tabs for Applications and Career Requests */}
      {record?.email && <Col xs={24}>
        <Card bordered={false}>
          <Tabs
            defaultActiveKey="applications"
            items={tabItems}
            size="large"
          />
        </Card>
      </Col>}
    </Row>
  );
};

const UsersShow = (props) => (
  <RestShow {...props} hasEdit resource="users">
    <UsersShowUI />
  </RestShow>
);

UsersShowUI.propTypes = {
  record: PropTypes.object,
};

export default UsersShow;
